/**
 * Component: Current-section tracking for the Browse Contents panel
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R29
 *
 * Tracks which guideline section the reader is in, so the Contents
 * tree can highlight it and open its top-level branch, and the R30
 * contents bar can name it (widget.md 4.11.2).
 *
 * "The section in view" is the last section whose top edge has
 * passed a reference line a quarter of the way down the scroll
 * root. An ``IntersectionObserver`` whose root margin shrinks the
 * observed area to a thin band at that line fires exactly when a
 * section boundary crosses it; the handler then re-derives the
 * current section from the sections' positions. No scroll listener
 * is involved, so a long section causes no work while it scrolls.
 *
 * The scroll root is resolved the same way the back-to-top control
 * does (``findScrollContainer``): the document, or a host element
 * that scrolls the widget itself. Without ``IntersectionObserver``
 * (very old browsers, jsdom) the hook reports no current section
 * and the panel simply shows no highlight.
 *
 * Risk Links: R7
 * FMEA Links: FM7
 */

import { useEffect, useState } from 'react';

import { findScrollContainer } from './scroll-container';

/** Fraction of the scroll root's height where the reference line sits. */
export const CURRENT_SECTION_LINE = 0.25;

/**
 * Pick the current section from section top edges measured relative
 * to the reference line (negative = above it). Exported for tests.
 *
 * Returns the last section whose top is at or above the line; if
 * the reader is above the first section, the first section.
 */
export function pickCurrentSection(
  tops: ReadonlyArray<{ sectionId: number; topFromLine: number }>,
): number | null {
  if (tops.length === 0) return null;
  let current = tops[0]!.sectionId;
  for (const { sectionId, topFromLine } of tops) {
    if (topFromLine <= 0) {
      current = sectionId;
    } else {
      break;
    }
  }
  return current;
}

/**
 * Return the sectionId of the section currently in view, or ``null``
 * before the first measurement / when tracking is unavailable.
 * ``sectionIds`` must be in source (DOM) order.
 */
export function useCurrentSection(sectionIds: readonly number[]): number | null {
  const [current, setCurrent] = useState<number | null>(null);
  // Stable dependency: the id list's contents, not its identity.
  const key = sectionIds.join(',');

  useEffect(() => {
    setCurrent(null);
    if (sectionIds.length === 0) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const elements = sectionIds
      .map((id) => document.getElementById(`cnt_${id}`))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    const scroller = findScrollContainer(elements[0]!);

    const recompute = (): void => {
      const rootTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const rootHeight = scroller ? scroller.clientHeight : window.innerHeight;
      const line = rootTop + rootHeight * CURRENT_SECTION_LINE;
      const tops = elements.map((el) => ({
        sectionId: Number(el.dataset.galenSectionId ?? el.id.slice('cnt_'.length)),
        topFromLine: el.getBoundingClientRect().top - line,
      }));
      setCurrent(pickCurrentSection(tops));
    };

    const pct = CURRENT_SECTION_LINE * 100;
    const observer = new IntersectionObserver(recompute, {
      root: scroller,
      // A 1%-tall band at the reference line.
      rootMargin: `-${pct}% 0px -${99 - pct}% 0px`,
      threshold: [0, 1],
    });
    elements.forEach((el) => observer.observe(el));
    recompute();
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return current;
}
